import React, { useState } from 'react';
import { IDetectedBarcode, Scanner } from '@yudiel/react-qr-scanner';
import { useTranslation } from '../../hooks/useTranslation';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { QrCode, Camera, AlertCircle, CheckCircle } from 'lucide-react';

interface ModernQRScannerDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (customerId: string, otp: string) => void;
  onError: (error?: string) => void;
}

export const ModernQRScannerDialog: React.FC<ModernQRScannerDialogProps> = ({
  open,
  onClose,
  onSuccess,
  onError,
}) => {
  const { translate } = useTranslation();
  const [isScanning, setIsScanning] = useState(false);
  const [lastScannedData, setLastScannedData] = useState<string | null>(null);

  const handleScan = (detectedCodes: IDetectedBarcode[]) => {
    try {
      setIsScanning(true);
      setLastScannedData(detectedCodes[0].rawValue);

      const url = new URL(detectedCodes[0].rawValue);
      const code = url.searchParams.get('code');
      const [customerId, otp] = code?.split(':') ?? [];

      if (customerId && otp) {
        onSuccess(customerId, otp);
        onClose();
      } else {
        onError('Invalid QR code format');
      }
    } catch (error) {
      onError('Invalid QR code URL');
    } finally {
      setIsScanning(false);
    }
  };

  const handleScannerError = (error: any) => {
    const errorMessage =
      error instanceof Error ? error.message : 'Scanner error';
    onError(errorMessage);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            {translate('pos.orders.fields.scanQrCode')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Scanner Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Camera className="h-4 w-4" />
              <span className="text-sm text-muted-foreground">
                {translate('pos.orders.messages.scannerReady')}
              </span>
            </div>
            {isScanning && (
              <Badge variant="secondary" className="animate-pulse">
                {translate('pos.orders.messages.scanning')}
              </Badge>
            )}
          </div>

          <Separator />

          {/* Scanner Component */}
          <div className="relative">
            {open && (
              <div className="relative overflow-hidden rounded-lg border">
                <Scanner
                  onScan={handleScan}
                  onError={handleScannerError}
                  scanDelay={500}
                />
                {/* Scanning overlay */}
                {isScanning && (
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                    <div className="bg-white rounded-lg p-4 shadow-lg">
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm font-medium">
                          {translate('pos.orders.messages.processing')}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="space-y-2">
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">
                  {translate('pos.orders.messages.scannerInstructions')}
                </p>
                <ul className="mt-1 space-y-1 text-xs">
                  <li>
                    • {translate('pos.orders.messages.pointCamera')}
                  </li>
                  <li>
                    •{' '}
                    {translate('pos.orders.messages.ensureGoodLighting')}
                  </li>
                  <li>• {translate('pos.orders.messages.holdSteady')}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Last Scanned Data (for debugging) */}
          {lastScannedData && process.env.NODE_ENV === 'development' && (
            <div className="space-y-2">
              <Separator />
              <div className="text-xs">
                <p className="font-medium text-muted-foreground">
                  Last scanned data (dev only):
                </p>
                <p className="font-mono bg-muted p-2 rounded text-xs break-all">
                  {lastScannedData}
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              {translate('pos.common.action.cancel')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
